import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import type { DocumentKind, DocumentSide } from '@rental/domain'
import { format } from '@rental/i18n'
import { Button, Field, FilePicker, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Camera, ExternalLink } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { AUTH_MODE, api } from '../api'
import type { CanDo } from '../booking-can'
import { StatusChip } from '../components/StatusChip'
import { FIELD, FIELD_ROW, ProblemNotice, StaffSelect } from '../components/ui'
import { romeDay } from '../customer/privacy'
import { filePath } from '../files'
import { DocumentOpen } from '../files-ui'
import { useLocale } from '../i18n'
import { photoToJpeg } from '../photos/jpeg'
import { sendPhoto } from '../photos/send'

type StaffDocument = ApiSchemas['StaffDocument']
type ErasedDocument = ApiSchemas['StaffErasedDocument']

/** The sides each document has; any other document may be one page or two. */
const SIDES: Record<DocumentKind, readonly DocumentSide[]> = {
  id_card: ['front', 'back'],
  driving_licence: ['front', 'back'],
  passport: ['single'],
  other: ['single', 'front', 'back'],
}

function useDocumentLabel() {
  const { messages: m } = useLocale()
  return (document: { kind: DocumentKind; side: DocumentSide }) =>
    `${m.documentKinds[document.kind]}, ${m.documentSides[document.side]}`
}

/**
 * CUS-2: the ID and licence on file, sent by the customer or photographed at
 * the counter. Opening one is an explicit act, never a thumbnail: the server
 * logs every view of an identity document (ARCHITECTURE). The web app links
 * to it; the iPad app fetches it on the press and shows it itself (A1).
 * S8: a document erased since stays listed, with its day: there was one.
 */
export function DocumentList({
  documents,
  erased = [],
  can,
}: {
  documents: readonly StaffDocument[]
  erased?: readonly ErasedDocument[]
  /** S9: the booking's own answer; a trip opens its delivery's documents, the role every booking's. */
  can: CanDo
}) {
  const { locale, messages: m } = useLocale()
  const label = useDocumentLabel()
  const opens = can('viewDocuments')
  if (documents.length === 0 && erased.length === 0) {
    return <p className="text-text-muted">{m.documents.none}</p>
  }
  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {documents.map((document) => (
          <li key={document.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-medium">{label(document)}</span>
            <StatusChip tone={document.status === 'stored' ? 'success' : 'warning'}>
              {m.mediaStatus[document.status]}
            </StatusChip>
            <span className="text-sm text-text-muted">
              {document.source === 'staff' ? m.documents.atCounter : m.documents.fromCustomer}
            </span>
            {opens && document.status === 'stored' ? (
              AUTH_MODE === 'cookie' ? (
                <a
                  href={filePath({ kind: 'document', mediaId: document.mediaId })}
                  target="_blank"
                  rel="noopener"
                  aria-label={format(m.documents.openLabel, { document: label(document) })}
                  className="flex min-h-touch items-center gap-1"
                >
                  <ExternalLink aria-hidden="true" className="size-4" />
                  {m.documents.open}
                </a>
              ) : (
                <DocumentOpen document={document} label={label(document)} />
              )
            ) : null}
          </li>
        ))}
        {erased.map((document) => (
          <li key={document.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-medium">{label(document)}</span>
            <span className="text-sm text-text-muted">
              {format(m.documents.erased, { date: romeDay(locale, document.erasedAt) })}
            </span>
          </li>
        ))}
      </ul>
      {opens && documents.length > 0 ? (
        <p className="text-sm text-text-muted">{m.documents.openHint}</p>
      ) : null}
    </div>
  )
}

/**
 * J1 step 2: the agent photographs the customer's ID and licence with the
 * iPad's camera. The photo is shown here before it is saved, from the iPad's
 * memory, so checking it is readable opens nothing on the server. A fieldset,
 * not a form: it sits inside the hand-over's form.
 */
export function DocumentCapture({ code, can }: { code: string; can: CanDo }) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const label = useDocumentLabel()
  const [kind, setKind] = useState<DocumentKind>('id_card')
  const [side, setSide] = useState<DocumentSide>('front')
  const [photo, setPhoto] = useState<{ blob: Blob; url: string } | null>(null)
  const [invalid, setInvalid] = useState<string | null>(null)
  const [busy, setBusy] = useState<'reading' | 'saving' | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  // The preview's memory is freed when another photo replaces it or the form goes.
  useEffect(() => () => (photo ? URL.revokeObjectURL(photo.url) : undefined), [photo])

  if (!can('handOver')) return null

  function clearInput() {
    if (input.current) input.current.value = ''
  }

  async function choose(file: File | undefined) {
    setInvalid(null)
    setProblem(null)
    setSaved(null)
    setPhoto(null)
    if (!file) return
    setBusy('reading')
    const jpeg = await photoToJpeg(file)
    setBusy(null)
    if (!jpeg) {
      setInvalid(m.documents.unreadable)
      clearInput()
      return
    }
    setPhoto({ blob: jpeg, url: URL.createObjectURL(jpeg) })
  }

  async function save() {
    if (!photo) {
      setInvalid(m.documents.choosePhoto)
      input.current?.focus()
      return
    }
    setProblem(null)
    setBusy('saving')
    const link = await call(
      api.POST('/v1/staff/bookings/{code}/documents', {
        params: { path: { code } },
        body: { kind, side, contentType: 'image/jpeg', sizeBytes: photo.blob.size },
      }),
    )
    const failed = link.ok ? await sendPhoto(link.data.upload, photo.blob) : link.problem
    setBusy(null)
    if (failed) {
      if (!takeOver(failed)) setProblem(failed)
      return
    }
    setSaved(format(m.documents.saved, { document: label({ kind, side }) }))
    setPhoto(null)
    clearInput()
    await Promise.all(
      [
        ['booking', code],
        ['checkout', code],
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    )
  }

  return (
    // A form inside the section: a well, as SubPanel draws one (DESIGN.md, "A screen and its parts").
    <div className="rounded-sm bg-surface-well p-4">
      <fieldset className="flex min-w-0 flex-col gap-4">
        <legend className="mb-4 font-bold text-brand-strong text-lg">{m.documents.capture}</legend>
        <div className={FIELD_ROW}>
          <Field className={FIELD} label={m.documents.kind}>
            {(control) => (
              <StaffSelect
                {...control}
                value={kind}
                onChange={(event) => {
                  const next = event.target.value as DocumentKind
                  setKind(next)
                  setSide(SIDES[next][0] ?? 'single')
                }}
              >
                {(Object.keys(SIDES) as DocumentKind[]).map((value) => (
                  <option key={value} value={value}>
                    {m.documentKinds[value]}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
          <Field className={FIELD} label={m.documents.side}>
            {(control) => (
              <StaffSelect
                {...control}
                value={side}
                onChange={(event) => setSide(event.target.value as DocumentSide)}
              >
                {SIDES[kind].map((value) => (
                  <option key={value} value={value}>
                    {m.documentSides[value]}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
          <div className="basis-full">
            <FilePicker
              ref={input}
              label={m.documents.photo}
              icon={<Camera aria-hidden="true" className="size-6" />}
              hint={m.documents.photoHint}
              error={invalid}
              busy={busy === 'reading'}
              className="rounded-sm"
              accept="image/*"
              capture="environment"
              onChange={(event) => void choose(event.target.files?.[0])}
            />
          </div>
        </div>
        {photo ? (
          <img
            src={photo.url}
            alt={format(m.documents.preview, { document: label({ kind, side }) })}
            className="aspect-4/3 w-full max-w-xs rounded-sm border border-rule bg-bg object-contain"
          />
        ) : null}
        <div role="status">
          {saved ? (
            <Notice tone="success">
              <p>{saved}</p>
            </Notice>
          ) : null}
        </div>
        <ProblemNotice problem={problem} />
        <div>
          <Button
            variant="staff"
            busy={busy === 'saving'}
            disabled={busy === 'reading'}
            onClick={() => void save()}
          >
            {busy === 'saving' ? m.documents.saving : m.documents.save}
          </Button>
        </div>
      </fieldset>
    </div>
  )
}
